import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import Loader from '../components/Loader/Loader';
import InduccionService from '../axios_services/induccion.service';
import { ButtonInsert, ButtonSearch, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalInduccionActa from '../components/Modal/ModalInduccionActa';
import ModalInduccionCrear from '../components/Modal/ModalInduccionCrear';
import { FileText } from 'lucide-react';
import './Induccion.css';

const ESTADOS = {
    1: 'Pendiente',
    2: 'En proceso',
    3: 'Finalizado',
    4: 'Abandono de Inducción',
};

function Induccion() {
    const [state] = useContext(AppContext);
    const perfilId = Number(state.user?.perfil_id);
    const esAutorizado = [5, 8, 22, 26].includes(perfilId);
    const esLector = false;

    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [filtroPeriodo, setFiltroPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [modalActa, setModalActa] = useState({ isOpen: false, row: null });
    const [modalCrear, setModalCrear] = useState({ isOpen: false, row: null });

    const initialFetchDone = useRef(false);

    useEffect(() => {
        if (!initialFetchDone.current) {
            initialFetchDone.current = true;
            loadData();
        }
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const params = { estado: estadoFiltro };
            if (filtroPeriodo) params.periodo = filtroPeriodo;
            const resp = await InduccionService.getInducciones(params);
            setRegistros(resp.data || []);
        } catch (error) {
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    const formatFecha = (f) => {
        if (!f) return '-';
        return String(f).split('T')[0];
    };

    const formatHora = (h) => {
        if (!h) return '-';
        return String(h).slice(0, 5);
    };

    const formatFechaHora = (fecha, hora) => {
        if (!fecha || !hora) return '-';
        return `${formatFecha(fecha)} ${formatHora(hora)}`;
    };

    const handleFinalizar = async (payload) => {
        const { documento, id_induccion } = payload;
        await InduccionService.finalizar({ id_induccion }, documento);
        await loadData();
    };

    if (!esAutorizado) return <div className="caja-flujo-container">No tiene permisos para acceder.</div>;

    if (isLoading && registros.length === 0) return <Loader />;

    return (
        <div className="caja-flujo-container induccion-modulo">
            <h1>REGISTRO DE INDUCCIÓN</h1>

            <div className="filtros induccion-filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={filtroPeriodo}
                        onChange={(e) => setFiltroPeriodo(e.target.value)}
                    />
                </div>
                <div className="campo-fecha">
                    <label>Estado:</label>
                    <select className="form-control" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="1">Pendiente</option>
                        <option value="2">En proceso</option>
                        <option value="3">Finalizado</option>
                        <option value="4">Abandono de Inducción</option>
                    </select>
                </div>
            </div>

            <div className="filtros-actions">
                <ButtonSearch onClick={loadData} isLoading={isLoading} />
            </div>

            {!esLector && (
                <div className="nuevo-movimiento-section">
                    <ButtonInsert onClick={() => setModalCrear({ isOpen: true, row: null })} />
                </div>
            )}

            <div className="table-container">
                <Table hover size="sm" className="induccion-table">
                    <thead>
                        <tr>
                            <th style={{ width: '220px', minWidth: '220px' }}>Postulante</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Estado</th>
                            <th style={{ width: '180px', minWidth: '180px' }}>Primera inducción</th>
                            <th style={{ width: '180px', minWidth: '180px' }}>Segunda inducción</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Documento</th>
                            {!esLector && <th style={{ width: '120px', minWidth: '120px' }}>Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length > 0 ? (
                            registros.map((r) => {
                                const estado = Number(r.estado);
                                const tieneDoc =
                                    Number(r.tiene_documento) === 1 ||
                                    (r.documento_url && String(r.documento_url).trim() !== '');

                                const actaBloque = () => {
                                    if (tieneDoc && r.documento_url) {
                                        return (
                                            <button
                                                type="button"
                                                title="Ver documento"
                                                className="acta-icon-doc acta-icon-doc--ok"
                                                onClick={() => window.open(r.documento_url, '_blank')}
                                            >
                                                <FileText size={20} strokeWidth={2} aria-hidden />
                                            </button>
                                        );
                                    }

                                    if (esLector) return null;

                                    return (
                                        <button
                                            type="button"
                                            title="Subir documento (PDF)"
                                            className="acta-icon-doc acta-icon-doc--falta"
                                            onClick={() => setModalActa({ isOpen: true, row: r })}
                                        >
                                            <FileText size={20} strokeWidth={2} aria-hidden />
                                        </button>
                                    );
                                };

                                return (
                                    <tr key={r.id_induccion}>
                                        <td>
                                            <div className="induccion-colaborador">
                                                <div className="induccion-nombre">{r.nombre_postulante || '-'}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="induccion-estado">{ESTADOS[estado] || r.estado}</div>
                                        </td>
                                        <td>{formatFechaHora(r.primera_induccion_fecha, r.primera_induccion_hora)}</td>
                                        <td>{formatFechaHora(r.segunda_induccion_fecha, r.segunda_induccion_hora)}</td>
                                        <td>
                                            <div className="acta-cell">{actaBloque()}</div>
                                        </td>
                                        {!esLector && (
                                            <td>
                                                <div className="induccion-acciones">
                                                    <ButtonUpdate title="Editar" onClick={() => setModalCrear({ isOpen: true, row: r })} />
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={esLector ? 5 : 6} className="sin-data">
                                    No hay registros.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalInduccionActa
                isOpen={modalActa.isOpen}
                row={modalActa.row}
                onClose={() => setModalActa({ isOpen: false, row: null })}
                onSave={async (payload) => {
                    setIsLoading(true);
                    try {
                        await handleFinalizar(payload);
                    } catch (error) {
                        alert(error.response?.data?.message || 'Error al finalizar.');
                        await loadData();
                    } finally {
                        setIsLoading(false);
                    }
                }}
            />

            <ModalInduccionCrear
                isOpen={modalCrear.isOpen}
                row={modalCrear.row}
                onClose={(shouldReload) => {
                    setModalCrear({ isOpen: false, row: null });
                    if (shouldReload) loadData();
                }}
                onSave={async (payload) => {
                    setIsLoading(true);
                    try {
                        if (modalCrear.row) {
                            await InduccionService.actualizar({ id_induccion: modalCrear.row.id_induccion, ...payload });
                        } else {
                            await InduccionService.crear(payload);
                        }
                    } catch (error) {
                        throw error;
                    } finally {
                        setIsLoading(false);
                    }
                }}
            />
        </div>
    );
}

export default Induccion;
